import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import usePetImage from '~/hooks/image/usePetImage';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import { secondaryImageState } from '~/state/atoms/image/imageAtoms';
import { OverlayLabel, OverlayRow } from '../OverlayLabel';
import { BlendingModeSelector, ImageBlendSlider } from './BlendingOptions';
import PetFusionColorSelector from './BlendingOptions/PetFusionColorSelector';
import {
    PetBlendingLabel,
    PetBlendSlider,
} from './BlendingOptions/PetBlendingOptions';
import WindowingOptions from './WindowingOptions';

/**
 * Three fusion configurations drive what the operator sees above the
 * windowing controls:
 *
 * - `petFusion`     — PET on either side, blended with a CT/MR partner.
 *                     PET-specific palette + opacity controls.
 * - `genericFusion` — Non-PET two-image fusion. Standard blend mode &
 *                     palette selector.
 * - `petOnly`       — PET loaded as the only image. Just the PET
 *                     colormap selector (no opacity slider).
 * - `none`          — Single non-PET image; no blending UI.
 */
type BlendMode = 'petFusion' | 'genericFusion' | 'petOnly' | 'none';

const RegistrationLabel = ({ label }: { label: string }) => (
    <OverlayRow sx={{ alignItems: 'center', px: 0.25, ml: -0.25 }}>
        <OverlayLabel>Registration</OverlayLabel>
        <OverlayLabel color="secondary.main">{label}</OverlayLabel>
    </OverlayRow>
);

/**
 * Header row shown above the slider + windowing panel. Returns null
 * when no blend control applies (single non-PET image).
 */
const BlendingHeader = ({ mode }: { mode: BlendMode }): ReactNode => {
    switch (mode) {
        case 'petFusion':
            return <PetBlendingLabel />;
        case 'genericFusion':
            return <BlendingModeSelector />;
        case 'petOnly':
            return (
                <OverlayRow>
                    <PetFusionColorSelector />
                </OverlayRow>
            );
        case 'none':
            return null;
    }
};

const BlendingSlider = ({ mode }: { mode: BlendMode }): ReactNode => {
    switch (mode) {
        case 'petFusion':
            return <PetBlendSlider />;
        case 'genericFusion':
            return <ImageBlendSlider />;
        default:
            return null;
    }
};

const ImageOptions = () => {
    const secondaryImage = useRecoilValue(secondaryImageState);
    const { isPetSecondary, isPetPrimary } = usePetImage();
    const registrationLabel =
        useRegistrationState().selectedRegistration?.contentLabel;

    const mode: BlendMode = secondaryImage
        ? isPetSecondary || isPetPrimary
            ? 'petFusion'
            : 'genericFusion'
        : isPetPrimary
          ? 'petOnly'
          : 'none';

    return (
        <Box display="grid" gap={0.5}>
            {secondaryImage && registrationLabel && (
                <RegistrationLabel label={registrationLabel} />
            )}
            <BlendingHeader mode={mode} />
            <Box display="flex" gap={0.5}>
                <BlendingSlider mode={mode} />
                <WindowingOptions />
            </Box>
        </Box>
    );
};

export default ImageOptions;
